import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_CREATE_ACCOUNT, GQL_FINISH_REGISTER } from '~/gql/auth/auth'

function CreateAccount() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  const token = urlParams.get('token')

  const createAccountSchema = z.object({
    firstName: z.string().min(1, { message: t('input.required.message') }),
    lastName: z.string().min(1, { message: t('input.required.message') }),
    email: z.string().email({ message: t('input.email.message') }),
    password: z.string().min(1, { message: t('input.password') }),
    confirmPassword: z.string().min(1, { message: t('input.password-confirm') }),
  }).refine(data => data.password === data.confirmPassword, {
    message: t('password.not-match.message'),
    path: ['confirmPassword'],
  })

  type CreateAccountFormValues = z.infer<typeof createAccountSchema>

  const form = useForm<CreateAccountFormValues>({
    resolver: zodResolver(createAccountSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  const [finishRegister, { loading: loadingFinish }] = useMutation(GQL_FINISH_REGISTER)

  const [createAccount, { loading: loadingCreate }] = useMutation(GQL_CREATE_ACCOUNT)

  const onSubmit = async (values: CreateAccountFormValues) => {
    if (token) {
      try {
        const { data } = await finishRegister({
          variables: {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            password: values.password,
            token,
          },
        })
        if (data && data.finishRegister) {
          toast.success(t('good.register.message'))
          navigate('/')
        } else {
          toast.error(t('bad.register.message'))
        }
      } catch {
        toast.error(t('bad.register.message'))
      }
    } else {
      try {
        const { data } = await createAccount({
          variables: {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            password: values.password,
          },
        })
        if (data && data.register.__typename === 'UserInfo') {
          toast.success(t('good.register.message'))
          navigate('/')
        } else {
          toast.error(t('bad.register.message'))
        }
      } catch {
        toast.error(t('bad.register.message'))
      }
    }
  }

  if (loadingFinish || loadingCreate) {
    return (
      <p className="text-center">
        {t('common.loading')}
        {' '}
        ...
      </p>
    )
  }

  return (
    <div className="flex w-screen min-h-dvh flex-col items-center justify-center p-5">
      <Card className="sm:w-xl w-full">
        <CardHeader>
          <CardTitle className="text-2xl">{t('finish.register')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="firstName">{t('common.firstname')}</Label>
                        <Input
                          id="firstName"
                          type="text"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="lastName">{t('common.lastname')}</Label>
                        <Input
                          id="lastName"
                          type="text"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="email">{t('common.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="m@example.com"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="password">{t('common.password')}</Label>
                        <Input
                          id="password"
                          type="password"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="confirmPassword">{t('password.confirm')}</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full">
                <p className="text-background-foreground">{t('common.register')}</p>
              </Button>

              <div className="mt-4 text-center text-sm">
                <a
                  onClick={() => navigate('/')}
                  className="underline underline-offset-4 cursor-pointer"
                >
                  {t('common.login')}
                </a>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}

export default CreateAccount
